import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BadgeIcon from "@mui/icons-material/Badge";
import EditNoteIcon from "@mui/icons-material/EditNote";
import axios from "axios";

// ─── constants ────────────────────────────────────────────────────────────────

const MAX_MB = 5;
const ACCEPTED = "image/*,.pdf";

const REVIEW_FIELDS = [
  { key: "fname",   label: "First name",    sm: 6 },
  { key: "lname",   label: "Last name",     sm: 6 },
  { key: "dob",     label: "Date of birth", sm: 4 },
  { key: "state",   label: "State",         sm: 4 },
  { key: "address", label: "Address",       sm: 12 },
  { key: "email",   label: "Email",         sm: 6 },
  { key: "phone",   label: "Phone",         sm: 6 },
];

const MANUAL_FIELDS = [
  { key: "fname",   label: "First name",    sm: 6 },
  { key: "lname",   label: "Last name",     sm: 6 },
  { key: "email",   label: "Email",         sm: 6 },
  { key: "phone",   label: "Phone",         sm: 6 },
  { key: "address", label: "Address",       sm: 12 },
  { key: "state",   label: "State",         sm: 6 },
  { key: "dob",     label: "Date of birth", sm: 6 },
];

const EMPTY_FORM = {
  fname: "", lname: "", email: "", phone: "",
  dob: "", docNum: "", address: "", state: "",
};

// ─── helpers ──────────────────────────────────────────────────────────────────

function mapExtracted(data = {}) {
  return {
    fname:   data.first_name       || data.fname   || "",
    lname:   data.last_name        || data.lname   || "",
    email:   data.email            || "",
    phone:   data.phone_number     || data.phone   || "",
    dob:     data.date_of_birth    || data.dob     || "",
    address: data.address          || "",
    state:   data.state_in_address || data.state   || "",
  };
}

// ─── component ────────────────────────────────────────────────────────────────

/**
 * IDUploadForm
 *
 * view: "capture" | "review"
 * scannedOnce: tracks whether an ID was scanned — so Back from GuestSearchStep
 *              returns to review (with OCR data) not the empty drop zone.
 *
 * Props:
 *   onComplete({ form, scanURL, editedFields }) — advance to GuestSearchStep
 *   onCancel()                                  — close the dialog
 */
export default function IDUploadForm({ onComplete, onCancel, initialView = "capture",initialTab = 0, initialForm, initialScanURL }) {
  const [view, setView]     = useState(initialView);
  const [form, setForm]     = useState(initialForm || { ...EMPTY_FORM });
  const [scanURL, setScanURL] = useState(initialScanURL || null);
  const [tab, setTab]                 = useState(initialTab); // 0 = Scan, 1 = Manual
  const [dragging, setDragging]       = useState(false);
  const [scanning, setScanning]       = useState(false);
  const [captureError, setCaptureError] = useState("");
  const [edited, setEdited]           = useState(new Set());
  const [manual, setManual]           = useState(initialTab === 1 && initialForm ? initialForm : { ...EMPTY_FORM });
  const fileRef = useRef();

  // ── scan ─────────────────────────────────────────────────────────────────

  const processFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      setCaptureError(`File must be under ${MAX_MB} MB.`);
      return;
    }
    setCaptureError("");
    setScanURL(URL.createObjectURL(file));
    setScanning(true);

    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post("/api/upload-id", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Auto-advance to review as soon as Textract responds
      setForm(mapExtracted(res.data.extracted));
      setEdited(new Set());
      setView("review");
    } catch (err) {
      console.error(err);
      setCaptureError("Upload failed. Please try again.");
      setScanURL(null);
    } finally {
      setScanning(false);
    }
  };

  const handleFileChange = (e) => processFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files[0]);
  };

  // Recapture — clear everything and go back to drop zone
  const handleRecapture = () => {
    setScanURL(null);
    setForm({ ...EMPTY_FORM });
    setEdited(new Set());
    setCaptureError("");
    if (fileRef.current) fileRef.current.value = "";
    setView("capture");
  };

  // Manual — skip review, go straight to guest search
  const handleManualContinue = () => {
    onComplete({ form: manual, scanURL: null, editedFields: new Set(), mode: "manual" });
  };

  // ── review ────────────────────────────────────────────────────────────────

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setEdited((prev) => new Set(prev).add(key));
  };

  const handleReviewContinue = () => {
    onComplete({ form, scanURL, editedFields: edited, mode: "scan" });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — CAPTURE VIEW
  // ─────────────────────────────────────────────────────────────────────────

  if (view === "capture") {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        {/* Header */}
        <Box
          sx={{
            px: 3, py: 1,
            borderBottom: "1px solid", borderColor: "divider",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          <Typography variant="body" fontWeight={600}>
            Scan a government-issued ID or enter details manually
          </Typography>
          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setCaptureError(""); }}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": { minHeight: 36, fontSize: 13, textTransform: "none", px: 2 },
            }}
          >
            <Tab icon={<BadgeIcon sx={{ fontSize: 16}} />} iconPosition="start" label="Scan ID" />
            <Tab icon={<EditNoteIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Manual entry" />
          </Tabs>
        </Box>

        <Box sx={{ px: 3, py: 1 }}>

          {/* ── SCAN TAB ── */}
          {tab === 0 && (
            <>
              {captureError && <Alert severity="error" sx={{ mb: 2 }}>{captureError}</Alert>}

              {/* Scanning spinner */}
              {scanning ? (
                <Box sx={{ textAlign: "center", py: 7 }}>
                  <CircularProgress size={36} sx={{ mb: 2 }} />
                  <Typography fontWeight={500} fontSize={14}>Analyzing ID…</Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    AWS Textract is reading the document
                  </Typography>
                </Box>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
                    Upload a clear photo of a government-issued ID (driver's license, passport, state ID). Max {MAX_MB} MB.
                  </Alert>
                  <Box
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileRef.current?.click()}
                    sx={{
                      border: "1.5px dashed",
                      borderColor: dragging ? "primary.main" : "divider",
                      borderRadius: 2,
                      p: 6,
                      textAlign: "center",
                      cursor: "pointer",
                      bgcolor: dragging ? "primary.50" : "background.default",
                      transition: "all .2s",
                      "&:hover": { borderColor: "primary.main", bgcolor: "primary.50" },
                    }}
                  >
                    <input
                      ref={fileRef}
                      type="file"
                      accept={ACCEPTED}
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                    />
                    <UploadFileIcon sx={{ fontSize: 44, color: "text.secondary", mb: 1 }} />
                    <Typography fontWeight={500} fontSize={14}>
                      Drop ID here or click to browse
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      JPEG, PNG, PDF — up to {MAX_MB} MB
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}

          {/* ── MANUAL TAB ── */}
          {tab === 1 && (
            <>
              <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
                Enter guest details manually. A guest search is required before saving.
              </Alert>
              <Grid container spacing={2}>
                {MANUAL_FIELDS.map(({ key, label, sm }) => (
                  <Grid item xs={12} sm={sm} key={key}>
                    <TextField
                      label={label}
                      fullWidth
                      size="small"
                      value={manual[key]}
                      onChange={(e) => setManual((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
                <Button
                  variant="contained"
                  disabled={!manual.fname.trim() || !manual.lname.trim()}
                  onClick={handleManualContinue}
                  sx={{ textTransform: "none" }}
                >
                  Continue to guest search
                </Button>
              </Box>
            </>
          )}

        </Box>
      </Paper>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — REVIEW VIEW (scan path only)
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          px: 3, py: 1,
          borderBottom: "1px solid", borderColor: "divider",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
      >
        <Typography fontWeight={600} fontSize={15}>Review extracted information</Typography>
        {edited.size > 0 && (
          <Chip
            label={`${edited.size} field${edited.size !== 1 ? "s" : ""} edited`}
            color="warning"
            size="small"
            sx={{ fontSize: 12, flexShrink: 0 }}
          />
        )}
      </Box>

      <Box sx={{ p: 3 }}>
        {edited.size > 0 ? (
          <Alert severity="warning" sx={{ mb: 1, fontSize: 13 }}>
            Fields marked <strong>Edited</strong> will override OCR values when saved.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 1, fontSize: 13 }}>
            All fields were auto-filled from the scanned ID. Edit any incorrect values before continuing.
          </Alert>
        )}

        <Grid container spacing={2} alignItems="flex-start">
          {/* ID image */}
          <Grid item xs={12} md={4}>
            <Typography variant="overline" color="text.secondary" fontSize={11}>
              ID image
            </Typography>
            <Box
              sx={{
                border: "0.5px solid", borderColor: "divider", borderRadius: 1.5,
                overflow: "hidden", bgcolor: "background.default",
                aspectRatio: "16/9",
                display: "flex", alignItems: "center", justifyContent: "center",
                mt: 0.5,
              }}
            >
              {scanURL ? (
                <Box
                  component="img"
                  src={scanURL}
                  alt="Scanned ID"
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Box sx={{ textAlign: "center", color: "text.secondary" }}>
                  <BadgeIcon sx={{ fontSize: 32, mb: 0.5 }} />
                  <Typography variant="caption" display="block">No preview</Typography>
                </Box>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5} textAlign="center">
              Scanned via AWS Textract
            </Typography>
          </Grid>

          {/* Editable fields */}
          <Grid item xs={12} md={8}>
            <Typography variant="overline" color="text.secondary" fontSize={11}>
              Extracted fields
            </Typography>
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              {REVIEW_FIELDS.map(({ key, label, sm }) => {
                const isEdited = edited.has(key);
                return (
                  <Grid item xs={12} sm={sm} key={key}>
                    <TextField
                      label={
                        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          {label}
                          <Chip
                            label={isEdited ? "Edited" : "OCR"}
                            size="small"
                            color={isEdited ? "warning" : "info"}
                            sx={{ fontSize: 10, height: 18, "& .MuiChip-label": { px: 0.75 } }}
                          />
                        </Box>
                      }
                      fullWidth
                      size="small"
                      value={form[key] || ""}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      sx={
                        isEdited
                          ? { "& .MuiOutlinedInput-root fieldset": { borderColor: "warning.main" } }
                          : {}
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
          <Button
            onClick={handleRecapture}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Recapture
          </Button>
          <Button
            variant="contained"
            onClick={handleReviewContinue}
            sx={{ textTransform: "none" }}
          >
            Continue to guest search
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}